// ABOUTME: Chart renderer component for displaying data visualizations.
// ABOUTME: Supports Recharts for bar, line, and area charts with glassmorphism styling.

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

type ChartType = 'bar' | 'line' | 'area';

interface ChartData {
  label: string;
  value: number;
  [key: string]: string | number;
}

const CHART_COLORS = [
  '#14b8a6', // teal (primary brand)
  '#818CF8', // indigo/periwinkle
  '#F59E0B', // amber/gold
  '#F43F5E', // coral/rose
  '#60A5FA', // sky blue
  '#A78BFA', // violet/lavender
  '#FB923C', // tangerine
  '#34D399', // emerald
  '#E879F9', // fuchsia/pink
  '#06B6D4', // cyan
];

interface ChartRendererProps {
  /** Single-series data ({label, value}). Use this OR rawData, not both. */
  data?: ChartData[];
  /** Raw multi-series data (e.g., {hour: "12am", Monday: 63, Tuesday: 62}).
   *  When provided, auto-detects category key and numeric series. */
  rawData?: Record<string, any>[];
  type?: ChartType;
  title?: string;
  xAxisKey?: string;
  yAxisKey?: string;
  color?: string;
  height?: number;
}

/**
 * Detect category key (first string-valued) and series keys (numeric) from raw data.
 */
function detectMultiSeriesKeys(data: Record<string, any>[]): { categoryKey: string; seriesKeys: string[] } {
  if (!data.length) return { categoryKey: '', seriesKeys: [] };
  const first = data[0];
  let categoryKey = '';
  const seriesKeys: string[] = [];
  for (const [key, val] of Object.entries(first)) {
    if (typeof val === 'string' && !categoryKey) {
      categoryKey = key;
    } else if (typeof val === 'number') {
      seriesKeys.push(key);
    }
  }
  return { categoryKey, seriesKeys };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: string | number; name?: string; color?: string }>;
  label?: string;
}

// Format axis tick values to avoid label overflow. Abbreviates large numbers
// (e.g. 1200 → 1.2K, 2500000 → 2.5M) and keeps small numbers as-is.
function formatTickValue(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    const formatted = (value / 1_000_000_000).toFixed(1);
    return `${formatted.replace(/\.0$/, '')}B`;
  }
  if (abs >= 1_000_000) {
    const formatted = (value / 1_000_000).toFixed(1);
    return `${formatted.replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {
    const formatted = (value / 1_000).toFixed(1);
    return `${formatted.replace(/\.0$/, '')}K`;
  }
  return value.toLocaleString();
}

// Estimate the pixel width needed for y-axis tick labels based on the
// longest formatted tick string in the data.
function estimateYAxisWidth(data: ChartData[], yAxisKey: string): number {
  if (data.length === 0) return 40;
  let maxVal = 0;
  for (const d of data) {
    const v = Number(d[yAxisKey]) || 0;
    if (Math.abs(v) > maxVal) maxVal = Math.abs(v);
  }
  const formatted = formatTickValue(maxVal);
  // ~7px per character at fontSize 11, plus 8px padding
  return Math.max(40, formatted.length * 7 + 8);
}

const MAX_LABEL_CHARS = 14;

// Truncate a label to MAX_LABEL_CHARS, appending "…" if shortened.
// Full labels remain available in tooltips.
function truncateLabel(label: string): string {
  return label.length > MAX_LABEL_CHARS
    ? label.slice(0, MAX_LABEL_CHARS - 1) + '…'
    : label;
}

// Decide how to render x-axis labels based on data volume and label length.
// Few items with long labels → angle, truncate, and force-show all.
// Many items (time series) → let Recharts auto-thin, no angle.
function computeXAxisLayout(data: ChartData[], xAxisKey: string): { angle: number; bottomMargin: number; forceAllLabels: boolean } {
  if (data.length === 0) return { angle: 0, bottomMargin: 0, forceAllLabels: false };
  if (data.length > 20) return { angle: 0, bottomMargin: 0, forceAllLabels: false };
  const labels = data.map((d) => String(d[xAxisKey] ?? ''));
  const maxLen = Math.max(...labels.map((l) => l.length));
  const avgLen = labels.reduce((s, l) => s + l.length, 0) / labels.length;
  const needsAngle = labels.length > 4 && avgLen > 6 || maxLen > 12;
  if (needsAngle) {
    // Use truncated length for margin calculation so labels don't eat the chart
    const displayLen = Math.min(maxLen, MAX_LABEL_CHARS);
    const extraHeight = Math.min(displayLen * 3, 50);
    return { angle: -30, bottomMargin: extraHeight, forceAllLabels: true };
  }
  return { angle: 0, bottomMargin: 0, forceAllLabels: true };
}

// Custom tooltip with card styling
function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="card rounded-sm px-3 py-2 shadow-lg">
      <p className="text-xs text-surface-500 mb-1">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-sm font-medium text-surface-800">
          {entry.name && payload.length > 1 && (
            <span style={{ color: entry.color }} className="mr-1">{entry.name}:</span>
          )}
          {typeof entry.value === 'number'
            ? entry.value.toLocaleString()
            : entry.value}
        </p>
      ))}
    </div>
  );
}

export function ChartRenderer({
  data,
  rawData,
  type = 'bar',
  title,
  xAxisKey = 'label',
  yAxisKey = 'value',
  color = '#14b8a6',
  height = 200,
}: ChartRendererProps) {
  // Determine if we're rendering multi-series data
  const isMultiSeries = !!rawData && rawData.length > 0;
  const chartData = isMultiSeries ? rawData! : (data || []);

  const { categoryKey, seriesKeys } = isMultiSeries
    ? detectMultiSeriesKeys(rawData!)
    : { categoryKey: xAxisKey, seriesKeys: [yAxisKey] };

  const yWidth = estimateYAxisWidth(data || [], yAxisKey);
  const { angle: xAngle, bottomMargin, forceAllLabels } = computeXAxisLayout(data || [], xAxisKey);

  const commonProps = {
    data: chartData,
    margin: { top: 10, right: 10, left: 0, bottom: bottomMargin },
  };

  const xAxisCommonProps = {
    dataKey: xAxisKey,
    stroke: 'rgba(0, 0, 0, 0.1)',
    tick: { fill: '#78716c', fontSize: 11 },
    tickFormatter: truncateLabel,
    ...(forceAllLabels && { interval: 0 as const }),
    ...(xAngle !== 0 && {
      angle: xAngle,
      textAnchor: 'end' as const,
      height: 20 + bottomMargin,
    }),
  };

  const yAxisCommonProps = {
    stroke: 'rgba(0, 0, 0, 0.1)',
    tick: { fill: '#78716c', fontSize: 11 },
    tickFormatter: formatTickValue,
    width: yWidth,
  };

  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.05)" />
            <XAxis {...xAxisCommonProps} dataKey={isMultiSeries ? categoryKey : xAxisKey} />
            <YAxis {...yAxisCommonProps} />
            <Tooltip content={<CustomTooltip />} />
            {isMultiSeries && <Legend />}
            {isMultiSeries ? (
              seriesKeys.map((key, i) => (
                <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
              ))
            ) : (
              <Line
                type="monotone"
                dataKey={yAxisKey}
                stroke={color}
                strokeWidth={2}
                dot={{ fill: color, strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: color }}
              />
            )}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.05)" />
            <XAxis {...xAxisCommonProps} dataKey={isMultiSeries ? categoryKey : xAxisKey} />
            <YAxis {...yAxisCommonProps} />
            <Tooltip content={<CustomTooltip />} />
            {isMultiSeries && <Legend />}
            {isMultiSeries ? (
              seriesKeys.map((key, i) => (
                <Area key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={`${CHART_COLORS[i % CHART_COLORS.length]}26`} strokeWidth={2} />
              ))
            ) : (
              <>
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey={yAxisKey}
                  stroke={color}
                  strokeWidth={2}
                  fill="url(#colorGradient)"
                />
              </>
            )}
          </AreaChart>
        );

      default:
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.05)" />
            <XAxis {...xAxisCommonProps} dataKey={isMultiSeries ? categoryKey : xAxisKey} />
            <YAxis {...yAxisCommonProps} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
            {isMultiSeries && <Legend />}
            {isMultiSeries ? (
              seriesKeys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
              ))
            ) : (
              <Bar
                dataKey={yAxisKey}
                fill={color}
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              />
            )}
          </BarChart>
        );
    }
  };

  return (
    <div className="w-full">
      {title && (
        <div className="text-xs text-surface-500 mb-2 font-mono uppercase tracking-wider">
          {title}
        </div>
      )}
      <div style={{ height }} className="w-full">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Helper function to transform tool result data into chart format
export function transformToolResultToChartData(
  content: unknown
): ChartData[] | null {
  if (!Array.isArray(content)) return null;
  if (content.length === 0) return null;

  // Check if items have numeric values
  const firstItem = content[0];
  if (typeof firstItem !== 'object' || firstItem === null) return null;

  const keys = Object.keys(firstItem);
  const numericKey = keys.find(
    (k) => typeof (firstItem as Record<string, unknown>)[k] === 'number'
  );
  const labelKey = keys.find(
    (k) => typeof (firstItem as Record<string, unknown>)[k] === 'string'
  );

  if (!numericKey) return null;

  return content.map((item, index) => {
    const record = item as Record<string, unknown>;
    return {
      label: labelKey
        ? String(record[labelKey])
        : `Item ${index + 1}`,
      value: Number(record[numericKey]) || 0,
      ...record,
    } as ChartData;
  });
}
